import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { Link } from 'react-router-dom'
import {
  getMateriales, getMaterialesAlertas, getMovimientos,
  ajustarStock, crearMaterial, actualizarMaterial, eliminarMaterial,
  getProveedoresMateriales,
  CATEGORIAS, TIPOS_MOVIMIENTO, ESTADOS_STOCK,
} from '../../lib/materialesApi'

export default function Materiales() {
  const [materiales, setMateriales] = useState([])
  const [alertas, setAlertas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [verInactivos, setVerInactivos] = useState(false)
  // Modales
  const [movimientoModal, setMovimientoModal] = useState(null) // { material, tipo }
  const [historialModal, setHistorialModal] = useState(null) // material seleccionado
  const [editModal, setEditModal] = useState(null) // material o {} para nuevo

  useEffect(() => { load() }, [verInactivos])

  async function load() {
    setLoading(true)
    const [m, a] = await Promise.all([
      getMateriales({ incluirInactivos: verInactivos }),
      getMaterialesAlertas(),
    ])
    setMateriales(m)
    setAlertas(a)
    setLoading(false)
  }

  const alertasMap = useMemo(() => {
    return Object.fromEntries(alertas.map(a => [a.sku, a]))
  }, [alertas])

  const filtrados = useMemo(() => {
    let f = materiales
    if (filtro !== 'todos') {
      if (filtro === 'sin_stock') f = f.filter(m => m.stock_actual === 0)
      else if (filtro === 'bajo') f = f.filter(m => m.stock_actual > 0 && m.stock_actual < m.stock_minimo)
      else if (filtro === 'ok') f = f.filter(m => m.stock_actual >= m.stock_minimo)
      else f = f.filter(m => m.categoria === filtro)
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(m =>
        (m.sku || '').toLowerCase().includes(q) ||
        (m.nombre || '').toLowerCase().includes(q) ||
        (m.proveedor || '').toLowerCase().includes(q)
      )
    }
    return f
  }, [materiales, filtro, busqueda])

  const stats = useMemo(() => {
    const total = materiales.length
    const sin_stock = materiales.filter(m => m.stock_actual === 0).length
    const bajo = materiales.filter(m => m.stock_actual > 0 && m.stock_actual < m.stock_minimo).length
    const ok = materiales.filter(m => m.stock_actual >= m.stock_minimo).length
    const valor_total_siva = materiales.reduce((acc, m) => acc + (m.stock_actual * (parseFloat(m.precio_compra_siva) || 0)), 0)
    return { total, sin_stock, bajo, ok, valor_total_siva }
  }, [materiales])

  return (
    <AdminLayout>
      <div className="page" style={{ maxWidth: 1400 }}>
        <h1>🧰 Stock de materiales</h1>
        <p className="lead">Insumos operativos (packaging, sellado, oficina, vehículos) — NO vendibles al cliente.</p>
        {/* Stats top */}
        <div className="cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16, marginBottom: 24 }}>
          <StatCard label="Total materiales" value={stats.total} />
          <StatCard label="Sin stock" value={stats.sin_stock} color="#dc2626" />
          <StatCard label="Bajo mínimo" value={stats.bajo} color="#ea580c" />
          <StatCard label="OK" value={stats.ok} color="#16a34a" />
          <StatCard label="Valor inventario s/IVA" value={`$${stats.valor_total_siva.toLocaleString('es-UY', { minimumFractionDigits: 2 })}`} />
        </div>

        {/* Filtros + buscador */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
          <Chip active={filtro === 'todos'} onClick={() => setFiltro('todos')}>Todos ({materiales.length})</Chip>
          <Chip active={filtro === 'sin_stock'} onClick={() => setFiltro('sin_stock')} color="#dc2626">Sin stock ({stats.sin_stock})</Chip>
          <Chip active={filtro === 'bajo'} onClick={() => setFiltro('bajo')} color="#ea580c">Bajo mínimo ({stats.bajo})</Chip>
          <Chip active={filtro === 'ok'} onClick={() => setFiltro('ok')} color="#16a34a">OK ({stats.ok})</Chip>
          <span style={{ width: 12 }} />
          {CATEGORIAS.map(c => (
            <Chip key={c.value} active={filtro === c.value} onClick={() => setFiltro(c.value)}>
              {c.label}
            </Chip>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Buscar SKU, nombre, proveedor..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ flex: 1, minWidth: 260, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}
          />
          <label style={{ display: 'flex', gap: 6, fontSize: 13, alignItems: 'center' }}>
            <input type="checkbox" checked={verInactivos} onChange={e => setVerInactivos(e.target.checked)} />
            Ver inactivos
          </label>
          <button
            onClick={() => setEditModal({ activo: true, stock_actual: 0, stock_minimo: 0, unidades_por_paquete: 1, unidad_consumo: 'unidad', categoria: 'packaging' })}
            style={{ background: '#1A3461', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}
          >
            + Nuevo material
          </button>
        </div>

        {/* Tabla */}
        {loading ? <p>Cargando…</p> : (
          <div style={{ overflowX: 'auto', background: 'white', border: '1px solid #e2e8f0', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={th}>SKU</th>
                  <th style={th}>Nombre</th>
                  <th style={th}>Categoría</th>
                  <th style={th}>Presentación</th>
                  <th style={{ ...th, textAlign: 'right' }}>Stock actual</th>
                  <th style={{ ...th, textAlign: 'right' }}>Mínimo</th>
                  <th style={th}>Estado</th>
                  <th style={{ ...th, textAlign: 'right' }}>Costo s/IVA</th>
                  <th style={th}>Proveedor</th>
                  <th style={th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 ? (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
                    Sin resultados.
                  </td></tr>
                ) : filtrados.map(m => {
                  const alerta = alertasMap[m.sku]
                  const estado = alerta?.estado || 'ok'
                  const cat = CATEGORIAS.find(c => c.value === m.categoria)
                  return (
                    <tr key={m.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={td}><code>{m.sku}</code></td>
                      <td style={td}>
                        <div style={{ fontWeight: 500 }}>{m.nombre}</div>
                        {m.notas && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{m.notas.slice(0, 80)}</div>}
                      </td>
                      <td style={td}>{cat?.label || m.categoria}</td>
                      <td style={td}>{m.presentacion}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{m.stock_actual.toLocaleString()}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{m.stock_minimo}</td>
                      <td style={td}>
                        <EstadoPill estado={estado} />
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>${parseFloat(m.precio_compra_siva || 0).toFixed(2)}</td>
                      <td style={{ ...td, fontSize: 12, color: '#64748b' }}>{m.proveedor || '—'}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <BtnAccion onClick={() => setMovimientoModal({ material: m, tipo: 'entrada' })} color="#16a34a">+ Entrada</BtnAccion>
                          <BtnAccion onClick={() => setMovimientoModal({ material: m, tipo: 'salida' })} color="#0284c7">− Salida</BtnAccion>
                          <BtnAccion onClick={() => setMovimientoModal({ material: m, tipo: 'ajuste' })} color="#9333ea">⚙</BtnAccion>
                          <BtnAccion onClick={() => setHistorialModal(m)} color="#64748b">📜</BtnAccion>
                          <BtnAccion onClick={() => setEditModal(m)} color="#1A3461">✏️</BtnAccion>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL: Movimiento de stock */}
      {movimientoModal && (
        <MovimientoModal
          movimientoModal={movimientoModal}
          onClose={() => setMovimientoModal(null)}
          onSaved={() => { setMovimientoModal(null); load() }}
        />
      )}

      {/* MODAL: Historial */}
      {historialModal && (
        <HistorialModal
          material={historialModal}
          onClose={() => setHistorialModal(null)}
        />
      )}

      {/* MODAL: Editar/Crear material */}
      {editModal && (
        <EditModal
          material={editModal}
          onClose={() => setEditModal(null)}
          onSaved={() => { setEditModal(null); load() }}
        />
      )}
    </AdminLayout>
  )
}

// ============== Componentes auxiliares ==============

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
      <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || '#1A3461' }}>{value}</div>
    </div>
  )
}

function Chip({ active, onClick, children, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px',
        borderRadius: 999,
        border: '1px solid ' + (active ? (color || '#1A3461') : '#e2e8f0'),
        background: active ? (color || '#1A3461') : 'white',
        color: active ? 'white' : '#1A3461',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

function EstadoPill({ estado }) {
  const e = ESTADOS_STOCK[estado] || ESTADOS_STOCK.ok
  return (
    <span style={{ background: e.bg, color: e.color, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {e.label}
    </span>
  )
}

function BtnAccion({ onClick, color, children }) {
  return (
    <button onClick={onClick} style={{
      background: color, color: 'white', border: 'none',
      padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
      cursor: 'pointer',
    }}>{children}</button>
  )
}

const th = { padding: '12px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700 }
const td = { padding: '12px 14px', fontSize: 14, color: '#1A3461' }

// ============== Modal: Movimiento de stock ==============

function MovimientoModal({ movimientoModal, onClose, onSaved }) {
  const { material, tipo } = movimientoModal
  const [cantidad, setCantidad] = useState('')
  const [motivo, setMotivo] = useState('')
  const [usarPaquete, setUsarPaquete] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const cantidadFinal = useMemo(() => {
    const n = parseInt(cantidad) || 0
    if (tipo === 'entrada' && usarPaquete) {
      return n * (material.unidades_por_paquete || 1)
    }
    return n
  }, [cantidad, usarPaquete, tipo, material])

  const tipoConfig = TIPOS_MOVIMIENTO[tipo]

  async function submit(e) {
    e.preventDefault()
    if (!cantidadFinal || cantidadFinal <= 0) { setError('Cantidad inválida'); return }
    if (!motivo.trim()) { setError('El motivo es obligatorio'); return }

    setLoading(true)
    setError(null)
    const result = await ajustarStock({
      sku: material.sku,
      tipo,
      cantidad: cantidadFinal,
      motivo: motivo.trim(),
    })
    setLoading(false)
    if (result?.error) {
      setError(result.error)
    } else if (result?.success) {
      onSaved()
    }
  }

  return (
    <ModalShell onClose={onClose}>
      <h2 style={{ fontSize: 20, marginBottom: 4, fontWeight: 800 }}>{tipoConfig.icon} {tipoConfig.label} de stock</h2>
      <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>{material.sku} · {material.nombre}</p>

      <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
        <div>Stock actual: <strong style={{ fontSize: 18 }}>{material.stock_actual}</strong> {material.unidad_consumo}s</div>
        <div style={{ color: '#64748b', marginTop: 4 }}>Mínimo: {material.stock_minimo} · Presentación: {material.presentacion}</div>
      </div>

      <form onSubmit={submit}>
        <label style={lbl}>Cantidad</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <input
            type="number"
            min="1"
            value={cantidad}
            onChange={e => setCantidad(e.target.value)}
            autoFocus
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 15 }}
          />
          {tipo === 'entrada' && material.unidades_por_paquete > 1 && (
            <label style={{ display: 'flex', gap: 6, fontSize: 12, alignItems: 'center', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={usarPaquete} onChange={e => setUsarPaquete(e.target.checked)} />
              en paquetes (×{material.unidades_por_paquete})
            </label>
          )}
        </div>
        {tipo === 'entrada' && usarPaquete && cantidad && (
          <div style={{ fontSize: 12, color: '#16a34a', marginTop: -10, marginBottom: 16 }}>
            ✅ {cantidad} paquete{cantidad > 1 ? 's' : ''} × {material.unidades_por_paquete} = <strong>{cantidadFinal} unidades</strong>
          </div>
        )}
        {tipo === 'ajuste' && (
          <div style={{ fontSize: 12, color: '#9333ea', marginTop: -10, marginBottom: 16 }}>
            ⚙ Ajuste setea el stock al valor exacto: <strong>{cantidadFinal}</strong> unidades
          </div>
        )}

        <label style={lbl}>Motivo</label>
        <textarea
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          rows={2}
          placeholder={
            tipo === 'entrada' ? 'Ej: Compra Gime mayo 2026 · factura #123'
              : tipo === 'salida' ? 'Ej: Despacho semana 6/may'
              : tipo === 'perdida' ? 'Ej: Bolsas dañadas en transporte'
              : 'Ej: Conteo físico — corregir diferencia'
          }
          style={{ width: '100%', padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, fontFamily: 'inherit', fontSize: 14, marginBottom: 16 }}
          required
        />

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ ...btnSec }}>Cancelar</button>
          <button type="submit" disabled={loading} style={{ ...btnPri, background: tipoConfig.color }}>
            {loading ? 'Guardando…' : `Confirmar ${tipoConfig.label.toLowerCase()}`}
          </button>
        </div>
      </form>
    </ModalShell>
  )
}

// ============== Modal: Historial ==============

function HistorialModal({ material, onClose }) {
  const [movs, setMovs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getMovimientos(material.id, 50).then(m => { setMovs(m); setLoading(false) })
  }, [material.id])

  return (
    <ModalShell onClose={onClose} maxWidth={680}>
      <h2 style={{ fontSize: 20, marginBottom: 4, fontWeight: 800 }}>📜 Histórico — {material.sku}</h2>
      <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>{material.nombre}</p>

      {loading ? <p>Cargando…</p> : movs.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
          Sin movimientos registrados todavía.
        </div>
      ) : (
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                <th style={th}>Fecha</th>
                <th style={th}>Tipo</th>
                <th style={{ ...th, textAlign: 'right' }}>Cantidad</th>
                <th style={{ ...th, textAlign: 'right' }}>Stock</th>
                <th style={th}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {movs.map(mv => {
                const t = TIPOS_MOVIMIENTO[mv.tipo] || {}
                return (
                  <tr key={mv.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={td}>{new Date(mv.created_at).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td style={td}><span style={{ color: t.color, fontWeight: 600 }}>{t.icon} {t.label}</span></td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{mv.cantidad}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{mv.stock_resultante}</td>
                    <td style={{ ...td, fontSize: 12, color: '#64748b' }}>{mv.motivo || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  )
}

// ============== Modal: Editar/Crear ==============

function EditModal({ material, onClose, onSaved }) {
  const esNuevo = !material.id
  const [form, setForm] = useState({
    sku: material.sku || '',
    nombre: material.nombre || '',
    categoria: material.categoria || 'packaging',
    presentacion: material.presentacion || '',
    unidad_consumo: material.unidad_consumo || 'unidad',
    unidades_por_paquete: material.unidades_por_paquete || 1,
    proveedor: material.proveedor || '',
    proveedor_id: material.proveedor_id || '',
    precio_compra_siva: material.precio_compra_siva || 0,
    stock_actual: material.stock_actual || 0,
    stock_minimo: material.stock_minimo || 0,
    notas: material.notas || '',
    activo: material.activo !== false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [proveedores, setProveedores] = useState([])

  useEffect(() => {
    getProveedoresMateriales().then(setProveedores)
  }, [])

  function selectProveedor(id) {
    const p = proveedores.find(p => p.id === id)
    setForm(f => ({
      ...f,
      proveedor_id: id || null,
      proveedor: p ? p.nombre : '',
    }))
  }

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }))
  }

  async function submit(e) {
    e.preventDefault()
    if (!form.sku.trim() || !form.nombre.trim()) {
      setError('SKU y nombre son obligatorios'); return
    }
    setLoading(true)
    setError(null)
    // 2026-06-12: proveedor_id puede ser string vacío del select; Postgres rechaza "" como UUID. Convertimos a null.
    const payload = { ...form, proveedor_id: form.proveedor_id || null }
    const result = esNuevo ? await crearMaterial(payload) : await actualizarMaterial(material.id, payload)
    setLoading(false)
    if (result?.error) {
      setError(result.error.message || result.error)
    } else {
      onSaved()
    }
  }

  async function eliminar() {
    if (!confirm(`¿Eliminar ${form.sku}? Esto lo marca como borrado pero no afecta los movimientos históricos.`)) return
    const result = await eliminarMaterial(material.id)
    if (result?.error) setError(result.error.message || result.error)
    else onSaved()
  }

  return (
    <ModalShell onClose={onClose} maxWidth={620}>
      <h2 style={{ fontSize: 20, marginBottom: 20, fontWeight: 800 }}>
        {esNuevo ? '+ Nuevo material' : `✏️ Editar ${material.sku}`}
      </h2>

      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>SKU</label>
            <input value={form.sku} onChange={e => update('sku', e.target.value.toUpperCase())} style={inp} disabled={!esNuevo} required />
          </div>
          <div>
            <label style={lbl}>Nombre</label>
            <input value={form.nombre} onChange={e => update('nombre', e.target.value)} style={inp} required />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Categoría</label>
            <select value={form.categoria} onChange={e => update('categoria', e.target.value)} style={inp}>
              {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Presentación</label>
            <input value={form.presentacion} onChange={e => update('presentacion', e.target.value)} style={inp} placeholder="200 u/paquete" />
          </div>
          <div>
            <label style={lbl}>Unidad consumo</label>
            <input value={form.unidad_consumo} onChange={e => update('unidad_consumo', e.target.value)} style={inp} placeholder="unidad" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>U/paquete</label>
            <input type="number" min="1" value={form.unidades_por_paquete} onChange={e => update('unidades_por_paquete', parseInt(e.target.value) || 1)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Stock actual</label>
            <input type="number" min="0" value={form.stock_actual} onChange={e => update('stock_actual', parseInt(e.target.value) || 0)} style={inp} disabled={!esNuevo} title="Para modificar stock, usá los botones Entrada/Salida/Ajuste" />
          </div>
          <div>
            <label style={lbl}>Stock mínimo</label>
            <input type="number" min="0" value={form.stock_minimo} onChange={e => update('stock_minimo', parseInt(e.target.value) || 0)} style={inp} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Proveedor</label>
            <select value={form.proveedor_id || ''} onChange={e => selectProveedor(e.target.value)} style={inp}>
              <option value="">— elegir proveedor —</option>
              {proveedores.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}{p.contacto ? ' · ' + p.contacto : ''}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {proveedores.length === 0 ? (
                <>Ninguno configurado como proveedor de materiales todavía. <Link to="/admin/proveedores" style={{ color: '#00A8B5' }}>Crear o editar proveedores →</Link></>
              ) : (
                <>¿Falta uno? <Link to="/admin/proveedores" style={{ color: '#00A8B5' }}>Ir a Proveedores →</Link> y marcalo como "Materiales".</>
              )}
            </div>
          </div>
          <div>
            <label style={lbl}>Precio compra s/IVA</label>
            <input type="number" step="0.01" min="0" value={form.precio_compra_siva} onChange={e => update('precio_compra_siva', parseFloat(e.target.value) || 0)} style={inp} />
          </div>
        </div>

        <label style={lbl}>Notas</label>
        <textarea value={form.notas} onChange={e => update('notas', e.target.value)} rows={2} style={{ ...inp, fontFamily: 'inherit' }} />

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 14 }}>
          <input type="checkbox" checked={form.activo} onChange={e => update('activo', e.target.checked)} />
          Activo (visible en listas)
        </label>

        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 13, marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 24 }}>
          {!esNuevo && (
            <button type="button" onClick={eliminar} style={{ ...btnSec, color: '#b91c1c', borderColor: '#fecaca' }}>
              🗑️ Eliminar
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={onClose} style={btnSec}>Cancelar</button>
            <button type="submit" disabled={loading} style={btnPri}>
              {loading ? 'Guardando…' : esNuevo ? 'Crear material' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </form>
    </ModalShell>
  )
}

// ============== Modal shell + estilos ==============

function ModalShell({ children, onClose, maxWidth = 520 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
         onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 14, padding: 28, maxWidth, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
           onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

const lbl = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 6 }
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }
const btnPri = { background: '#1A3461', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }
const btnSec = { background: 'white', color: '#1A3461', border: '1px solid #e2e8f0', padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }
